import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { UndoRedoState, UndoRedoAction, ActionCategory } from './types';
import { undoRedoManager } from './undoRedoManager';

// Serializable version of UndoRedoAction for Redux state
interface SerializableAction {
    id: string;
    timestamp: number;
    description: string;
    category: ActionCategory;
    targetId?: string;
    targetName?: string;
    changes: Array<{
        path: string;
        oldValue: any;
        newValue: any;
        displayName: string;
    }>;
}

// Serializable version of HistoryEntry
interface SerializableHistoryEntry {
    action: SerializableAction;
    isActive: boolean;
    canUndoTo: boolean;
    canRedoTo: boolean;
}

interface UndoRedoSliceState {
    // Mirror the manager state for React components (serializable only)
    history: SerializableAction[];
    currentIndex: number;
    canUndo: boolean;
    canRedo: boolean;
    isUndoing: boolean;
    isRedoing: boolean;

    // UI state
    showHistoryPanel: boolean;
    historyEntries: SerializableHistoryEntry[];
}

const initialState: UndoRedoSliceState = {
    history: [],
    currentIndex: -1,
    canUndo: false,
    canRedo: false,
    isUndoing: false,
    isRedoing: false,
    showHistoryPanel: false,
    historyEntries: [],
};

const undoRedoSlice = createSlice({
    name: 'undoRedo',
    initialState,
    reducers: {
        // Sync with manager state
        syncWithManager: (
            state,
            action: PayloadAction<{
                history: SerializableAction[];
                currentIndex: number;
                maxHistorySize: number;
                isUndoing: boolean;
                isRedoing: boolean;
            }>,
        ) => {
            const serializableState = action.payload;

            // Use the already serializable history
            state.history = serializableState.history;
            state.currentIndex = serializableState.currentIndex;
            state.canUndo = serializableState.currentIndex >= 0;
            state.canRedo =
                serializableState.currentIndex <
                serializableState.history.length - 1;
            state.isUndoing = serializableState.isUndoing;
            state.isRedoing = serializableState.isRedoing;

            // Create serializable history entries from the serializable history
            state.historyEntries = serializableState.history.map(
                (action, index): SerializableHistoryEntry => ({
                    action,
                    isActive: index === serializableState.currentIndex,
                    canUndoTo: index <= serializableState.currentIndex,
                    canRedoTo: index > serializableState.currentIndex,
                }),
            );
        },

        // UI actions
        toggleHistoryPanel: (state) => {
            state.showHistoryPanel = !state.showHistoryPanel;
        },

        setHistoryPanelVisible: (state, action: PayloadAction<boolean>) => {
            state.showHistoryPanel = action.payload;
        },

        // These actions will trigger the manager, which will then sync back
        requestUndo: () => {
            // This is handled by middleware
        },

        requestRedo: () => {
            // This is handled by middleware
        },

        requestUndoTo: (state, action: PayloadAction<number>) => {
            // This is handled by middleware
        },

        requestRedoTo: (state, action: PayloadAction<number>) => {
            // This is handled by middleware
        },

        requestClearHistory: () => {
            // This is handled by middleware
        },
    },
});

export const {
    syncWithManager,
    toggleHistoryPanel,
    setHistoryPanelVisible,
    requestUndo,
    requestRedo,
    requestUndoTo,
    requestRedoTo,
    requestClearHistory,
} = undoRedoSlice.actions;

export default undoRedoSlice.reducer;

// Middleware to handle undo/redo requests
export const undoRedoMiddleware =
    (store: any) => (next: any) => (action: any) => {
        // Handle undo/redo requests
        switch (action.type) {
            case 'undoRedo/requestUndo':
                undoRedoManager.undo();
                break;
            case 'undoRedo/requestRedo':
                undoRedoManager.redo();
                break;
            case 'undoRedo/requestUndoTo':
                undoRedoManager.undoTo(action.payload);
                break;
            case 'undoRedo/requestRedoTo':
                undoRedoManager.redoTo(action.payload);
                break;
            case 'undoRedo/requestClearHistory':
                undoRedoManager.clear();
                break;
        }

        return next(action);
    };

// Helper to create serializable version of manager state
function createSerializableManagerState(managerState: UndoRedoState) {
    return {
        // Only include serializable properties
        history: managerState.history.map(
            (action: UndoRedoAction): SerializableAction => ({
                id: action.id,
                timestamp: action.timestamp,
                description: action.description,
                category: action.category,
                targetId: action.targetId,
                targetName: action.targetName,
                changes: action.changes,
            }),
        ),
        currentIndex: managerState.currentIndex,
        maxHistorySize: managerState.maxHistorySize,
        isUndoing: managerState.isUndoing,
        isRedoing: managerState.isRedoing,
    };
}

// Helper to setup the manager subscription
export function setupUndoRedoSync(dispatch: any) {
    return undoRedoManager.subscribe((managerState) => {
        const serializableState = createSerializableManagerState(managerState);
        dispatch(syncWithManager(serializableState));
    });
}
